export interface TypedButtonDefinition<T> {
	value: T;
	button?: HTMLElement;
	selected?: boolean
}

export class ButtonRadioGroup<T extends string | number | boolean> {
	private readonly buttons: Map<HTMLElement, T>;
	private value: T;
	private onValueChangedHandler: (t: T) => void = () => {};

	private constructor(definitions: TypedButtonDefinition<T>[]) {
		this.buttons = new Map();
		// preselect a configured option only if exactly one is configured
		const preselect = definitions.filter(d => d.selected).length === 1;
		definitions.forEach(definition => {
			const button = definition.button;
			if (button) {
				// a server-rendered btn-primary carries the persisted selection for groups configured without "selected"
				if (button.classList.contains("btn-primary") || (preselect && definition.selected)) {
					this.value = definition.value;
				}
				this.buttons.set(button, definition.value);
			}
		});

		// let the single class-owning method render the initial state, so a preselected button
		// cannot end up carrying btn-default and btn-primary at once
		this.updateActiveButton();

		this.buttons.forEach((value, button) => button.addEventListener("click", () => this.changeValue(value)));
	}

	private changeValue(value: T) {
		if (this.value !== value) {
			this.value = value;
			this.updateActiveButton();
			this.onValueChangedHandler(value);
		}
	}

	private updateActiveButton() {
		this.buttons.forEach((value, button) => {
			button.classList.toggle("btn-primary", value === this.value);
			button.classList.toggle("btn-default", value !== this.value);
		});
	}

	public onValueChanged(handler: (t: T) => void): this {
		this.onValueChangedHandler = handler;
		return this;
	}

	public getValue(): T {
		return this.value;
	}

	public setValue(value: T) {
		this.changeValue(value);
	}

	public static typed<G extends string | number | boolean>(...definitions: TypedButtonDefinition<G>[]): ButtonRadioGroup<G> {
		return new ButtonRadioGroup<G>(definitions);
	}

	public setVisible(visible: boolean) {
		if (visible) {
			this.buttons.forEach((_value, button) => button.style.display = "initial");
		} else {
			this.buttons.forEach((_value, button) => button.style.display = "none");
		}
	}
}
